import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * LAS CUENTAS DONDE SE LE LIQUIDA A UN COMERCIO — pantalla propia, en
 * `console/merchants/:code/cuentas`.
 *
 * 🔴 Era la pestaña `cuenta` de la ficha del comercio. Con ruta propia se carga
 * sola: entrar a mirar dónde se le deposita ya no arrastra las tiendas, los
 * clientes ni los usuarios del comercio.
 *
 * Lo que se decide aquí: SI EL COMERCIO PUEDE COBRAR. Sin ninguna cuenta activa
 * no se le puede liquidar, y por eso ese estado es la cinta de la pantalla —el
 * único aviso destacado que hay—.
 *
 * 📍 Hay exactamente UNA cuenta principal: la que se usa si nadie elige otra al
 * liquidarle. Marcar una apaga la anterior, y eso lo resuelve el servidor.
 *
 * ⚠️ NO hay filtros: son unas pocas cuentas por comercio y se muestran todas,
 * también las dadas de baja —atenuadas— porque hacen falta para saber a dónde
 * se le pagó antes. Por eso esta pantalla no escribe nada en la dirección.
 *
 * ⚠️ Cero endpoints nuevos: `GET /api/merchant/:code` (el nombre, para la miga
 * de pan) y los cuatro de `payment-accounts` que ya existían.
 */
@Component({
  selector: 'app-comercios-cuentas',
  templateUrl: './comercios-cuentas.component.html',
  styleUrls: ['./comercios-cuentas.component.css']
})
export class ComerciosCuentasComponent implements OnInit {

  codigo = '';
  comercio: any = null;

  cargando = true;
  noEncontrado = false;

  cuentas: any[] = [];
  cuentasCargadas = false;
  puedeLiquidarse = false;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });
  }

  /** 3.5 (08-oct): las cuentas tienen sus permisos propios. Cargar/editar: tesorero y gerente;
   *  verificar: solo la gerente (config.comercios_cuentas.verify). */
  get puedeEditar(): boolean { return this._auth.hasPermission('config.comercios_cuentas.update'); }
  get puedeVerificar(): boolean { return this._auth.hasPermission('config.comercios_cuentas.verify'); }
  puede = (_accion: string): boolean => this.puedeEditar;

  /** Sin `trackBy` los botones de la fila dejan de responder cuando Angular la
   *  rehace: el `mousedown` cae en un nodo y el `mouseup` en otro. */
  porId = (_i: number, c: any): string => c?._id;

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
    if (!this.noEncontrado) await this.cargarCuentas();
  };

  cargarCuentas = async (): Promise<void> => {
    try {
      const rep = await this._merchant.getPaymentAccounts(this.codigo);
      this.cuentas = rep?.accounts || [];
      this.puedeLiquidarse = !!rep?.puede_liquidarse;
      this.cuentasCargadas = true;
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudieron cargar las cuentas',
                  text: e?.error?.msg || e?.message });
    }
  };

  get cuentasActivas(): any[] { return this.cuentas.filter(c => c.status !== false); }

  /** Ninguna cuenta activa puede recibir pagos (está por verificar o rechazada): la cinta lo dice. */
  get ningunaPagable(): boolean {
    return this.cuentasActivas.length > 0 && !this.cuentasActivas.some(c => c.para_pagar?.puede);
  }

  // ══ Verificar (3.5, CTA-02) ════════════════════════════════════════════════
  //
  // Cargar no es verificar: la carga una persona y la verifica OTRA con la constancia del banco.
  // Se hace en la misma fila, sin diálogos. Rechazar pide motivo.

  verificando: string | null = null;     // _id de la cuenta con el panel abierto
  constancia = '';
  motivo = '';
  enviandoVerificacion = false;
  errorVerificacion = '';
  avisos: string[] = [];
  historialAbierto: string | null = null;

  /** La cargó el usuario de la sesión: no puede verificarla él mismo. */
  laCargueYo = (c: any): boolean => !!this._auth.sessionUid && c?.verification?.loaded_by === this._auth.sessionUid;

  abrirVerificacion = (c: any): void => {
    this.verificando = this.verificando === c._id ? null : c._id;
    this.constancia = ''; this.motivo = ''; this.errorVerificacion = '';
  };

  verificar = async (c: any): Promise<void> => {
    if (this.enviandoVerificacion) return;
    this.enviandoVerificacion = true; this.errorVerificacion = '';
    try {
      const rep = await this._merchant.verifyPaymentAccount(this.codigo, c._id, this.constancia.trim());
      this.verificando = null;
      await this.cargarCuentas();   // el estado para pagar lo calcula el servidor
      void rep;
    } catch (e: any) {
      this.errorVerificacion = e?.error?.msg || 'No se pudo verificar la cuenta.';
    } finally { this.enviandoVerificacion = false; }
  };

  rechazar = async (c: any): Promise<void> => {
    if (this.enviandoVerificacion) return;
    if (!this.motivo.trim()) { this.errorVerificacion = 'Para rechazarla escribe el motivo.'; return; }
    this.enviandoVerificacion = true; this.errorVerificacion = '';
    try {
      await this._merchant.rejectPaymentAccount(this.codigo, c._id, this.motivo.trim());
      this.verificando = null;
      await this.cargarCuentas();
    } catch (e: any) {
      this.errorVerificacion = e?.error?.msg || 'No se pudo rechazar la cuenta.';
    } finally { this.enviandoVerificacion = false; }
  };

  verHistorial = (c: any): void => { this.historialAbierto = this.historialAbierto === c._id ? null : c._id; };

  /** El paso del historial en palabras. */
  accion = (a: string): string => ({
    CARGAR: 'Cargó la cuenta', CARGA_HISTORICA: 'Estaba cargada antes de la verificación',
    VERIFICAR: 'Verificó la cuenta', RECHAZAR: 'Rechazó la cuenta', CORREGIR: 'Corrigió la cuenta',
    DAR_DE_BAJA: 'Dio de baja la cuenta',
  } as any)[a] || a;

  /** Quién, sin mostrar ids: «ti», «carga histórica» u «otra persona» (se lee «por ti»). */
  quien = (u: string): string =>
    !u ? '—' : u === 'CARGA_HISTORICA' ? 'carga histórica' : u === this._auth.sessionUid ? 'ti' : 'otra persona';

  // ══ El alta ═══════════════════════════════════════════════════════════════
  //
  // El formulario vive EN LA PÁGINA, no en un diálogo: son seis campos y
  // necesitan etiqueta. Un modal de sweetalert con inputs crudos no usa el
  // sistema de diseño y se ve de otra aplicación.

  formCuenta = false;
  guardandoCuenta = false;
  cuentaNueva: any = { account_number: '', alias: '', holder: '', tax_id: '',
                       account_type: 'CORRIENTE', currency_code: 'VES', is_default: false };

  abrirFormCuenta = (): void => {
    // 2026-09-24: el titular y su RIF vienen precargados con los del propio comercio, que es el
    // caso normal; se cambian si la cuenta es de otro titular.
    this.cuentaNueva = { account_number: '', alias: '',
                         holder: this.comercio?.legal_name || this.comercio?.name || '',
                         tax_id: this.comercio?.tax_id || '',
                         account_type: 'CORRIENTE', currency_code: 'VES', is_default: false };
    this.formCuenta = true;
  };

  cerrarFormCuenta = (): void => { this.formCuenta = false; };

  /** El tipo en palabras: la lista mostraba el código («CORRIENTE»). */
  tipoCuenta = (t: string): string =>
    ({ CORRIENTE: 'Corriente', AHORRO: 'Ahorro', '00': 'Corriente', '01': 'Ahorro' } as any)[String(t || '').toUpperCase()] || t || '—';

  get cuentaValida(): boolean { return !!this.cuentaNueva.account_number?.trim(); }

  guardarCuenta = async (): Promise<void> => {
    if (!this.cuentaValida || this.guardandoCuenta) return;
    this.guardandoCuenta = true;
    try {
      const rep = await this._merchant.addPaymentAccount(this.codigo, {
        ...this.cuentaNueva,
        account_number: this.cuentaNueva.account_number.trim()
      });
      // 📍 La respuesta ya trae la lista entera: no se vuelve a pedir la ficha
      // del comercio, que era lo que hacía la pestaña vieja para refrescar un
      // dato que esta pantalla ni muestra.
      // Avisos del servidor que no frenan (titular o RIF distintos a los del comercio).
      this.avisos = rep?.avisos || [];
      this.formCuenta = false;
      await this.cargarCuentas();   // trae el estado para pagar de cada cuenta
    } catch (e: any) {
      // El formulario no se cierra: lo que se escribió se queda.
      Swal.fire({ icon: 'error', title: 'No se pudo agregar la cuenta',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.' });
    } finally { this.guardandoCuenta = false; }
  };

  /** Marcar principal apaga la anterior: hay exactamente una. */
  marcarPrincipal = async (c: any): Promise<void> => {
    if (c.is_default) return;
    try {
      const rep = await this._merchant.setDefaultPaymentAccount(this.codigo, c._id);
      this.cuentas = rep?.accounts || this.cuentas;
      Swal.fire({ icon: 'success', title: 'Es la cuenta principal',
                  text: rep?.aviso, timer: 2200, showConfirmButton: false });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar la cuenta principal',
                  text: e?.error?.msg || e?.message });
    }
  };

  /**
   * 🔴 Dar de baja la principal deja al comercio sin cuenta si no hay otra.
   * El servidor promueve una y lo avisa; aquí se muestra ese aviso.
   */
  bajarCuenta = async (c: any): Promise<void> => {
    const r = await Swal.fire({
      icon: 'warning', title: '¿Dar de baja esta cuenta?',
      html: `<b>${c.account_number}</b><br><br>Deja de usarse, pero <b>queda registrada</b> para ` +
            'poder saber a dónde se le pagó antes.' +
            (c.is_default ? '<br><br>⚠️ <b>Es la cuenta principal.</b> Si hay otra activa pasa a ser la principal; si no, el comercio queda sin cuenta de liquidación.' : ''),
      showCancelButton: true, confirmButtonText: 'Dar de baja la cuenta', cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    try {
      const rep = await this._merchant.removePaymentAccount(this.codigo, c._id);
      this.cuentas = rep?.accounts || this.cuentas;
      this.puedeLiquidarse = this.cuentasActivas.length > 0;
      Swal.fire({ icon: rep?.aviso?.includes('SIN') ? 'warning' : 'success',
                  title: 'Cuenta dada de baja', text: rep?.aviso });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo dar de baja la cuenta',
                  text: e?.error?.msg || e?.message });
    }
  };
}
